
import React, { createContext, useContext } from "react";
import { User } from "@supabase/supabase-js";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import { Profile } from "@/lib/supabase-types";

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    user, 
    profile, 
    isLoading, 
    signIn, 
    signUp, 
    signOut, 
    updateProfile 
  } = useSupabaseAuth();

  const authContextValue: AuthContextType = {
    user,
    profile,
    isAuthenticated: !!user,
    isLoading,
    login: signIn,
    signup: signUp,
    logout: signOut,
    updateUser: updateProfile,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
